/* eslint-disable */
import React, { FC, forwardRef } from 'react';
import styled from 'styled-components';
import { InputProps } from 'antd';
import { Rule } from 'antd/lib/form';
import Form from '~/components/WizardForm';
import colors from '~/constants/colors';
import { useDirection } from '~/hooks/i18nHooks';
import saudiFlag from '../img/icons/saudiArabiaFlag.svg';

const COUNTRY_CODE = '+966';

interface Props {
  name: string | number | (string | number)[];
  label?: string;
  placeholder?: string;
  rules?: Rule[];
  className?: InputProps['className'];
  style?: InputProps['style'];
  disabled?: InputProps['disabled'];
  required?: boolean;
  autoComplete?: InputProps['autoComplete'];
  noHelp?: boolean;
}

/* ─── Custom input that antd Form passes value/onChange into ─── */
interface CustomInputProps {
  value?: string;
  onChange?: (val: string) => void;
  disabled?: boolean;
  hasError?: boolean;
  dir?: string;
  placeholder?: string;
  id?: string;
}

export const CustomPhoneInput = forwardRef<HTMLInputElement, CustomInputProps>(
  ({ value, onChange, disabled, hasError, dir, placeholder, id }, ref) => {
    // Strip the country-code prefix from the stored value so only
    // the local digits appear in the <input>.
    const localNumber =
      value?.startsWith(COUNTRY_CODE) ? value.slice(COUNTRY_CODE.length) : value ?? '';

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      // Allow only digits
      const digits = e.target.value.replace(/\D/g, '');
      // If user cleared all digits, emit undefined so the required rule fires correctly.
      onChange?.(digits ? COUNTRY_CODE + digits : undefined as any);
    };

    const defaultPlaceholder = dir === 'rtl' ? '5 XXX XX  XXX مثال' : '123-457';
    const computedPlaceholder = placeholder ?? defaultPlaceholder;

    return (
      <PhoneWrapper id={id} hasError={hasError} dir={dir}>
        {/* Left prefix section: flag + dial code */}
        <PrefixSection>
          <FlagIcon src={saudiFlag} alt="Saudi Arabia flag" />
          <DialCode>+966</DialCode>
        </PrefixSection>

        {/* Vertical separator */}
        <Separator />

        {/* Number input — user types only local digits */}
        <NumberInput
          id={id ? `${id}_input` : undefined}
          ref={ref}
          type="tel"
          value={localNumber}
          onChange={handleChange}
          disabled={disabled}
          dir="ltr"          /* phone numbers are always LTR */
          maxLength={9}      /* Saudi local numbers are 9 digits */
          placeholder={computedPlaceholder}
          autoComplete="tel-national"
        />
      </PhoneWrapper>
    );
  }
);

/* ─── Main exported form item ─── */
const PhoneNumberFormItem: FC<Props> = (props) => {
  const { name, rules, style, disabled, required, placeholder } = props;
  const dir = useDirection();

  return (
    <Form.Item shouldUpdate>
      {(form: any) => {
        const hasError = (form.getFieldError(name) || []).length > 0;
        return (
          <Form.Item
            name={name}
            rules={rules}
            required={required}
            style={{ marginBottom: -8, ...style }}
          >
            <CustomPhoneInput disabled={disabled} hasError={hasError} dir={dir} placeholder={placeholder} />
          </Form.Item>
        );
      }}
    </Form.Item>
  );
};

/* ─────────────────  Styled components  ───────────────── */

const PhoneWrapper = styled.div<{ hasError?: boolean; dir?: string }>`
  display: flex;
  align-items: center;
  direction: ltr;
  width: 100%;
  max-width: 570px;
  min-width: 0;
  height: 60px;
  min-height: 60px;
  box-sizing: border-box;
  border: 1px solid
    ${({ hasError }) => (hasError ? colors.errorColor : '#d9d9d9')};
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus-within {
    /* Match project's inputHoverBorderColor (#ABC8E9) */
    border-color: ${({ hasError }) => (hasError ? colors.errorColor : '#ABC8E9')};
    box-shadow: 0 0 0 2px
      ${({ hasError }) =>
    hasError ? 'rgba(245,34,45,0.1)' : 'rgba(171,200,233,0.3)'};
  }
`;

const PrefixSection = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px 0 12px;
  flex-shrink: 0;
  height: 100%;
`;

const FlagIcon = styled.img`
  width: 28px;
  height: 20px;
  object-fit: cover;
  border-radius: 3px;
  flex-shrink: 0;
`;

const DialCode = styled.span`
  font-size: 14px;
  font-weight: 400;
  color: #000;
  white-space: nowrap;
  line-height: 1;
`;

const Separator = styled.div`
  width: 1.5px;
  height: 30px;        /* ~40 % of the 56px row height */
  background: #d9d9d9;
  flex-shrink: 0;
`;

const NumberInput = styled.input`
  flex: 1;
  height: 100%;
  border: none;
  outline: none;
  padding: 0 12px;
  font-size: 14px;
  color: #000;
  // background: transparent;
  // /* Hide blinking text cursor */
  // caret-color: transparent;

  &::placeholder {
    color: #bfbfbf;
  }

  &:disabled {
    background: transparent;
    color: #00000040;
    cursor: not-allowed;
  }
`;

export default PhoneNumberFormItem;
